<template>
  <b-overlay 
    :show="isBusy" 
    variant="dark"
  >
    <b-card 
      v-if="unit" 
      no-body
    >
      <b-card-header>
        <b-form-row>
          <b-col 
            cols="auto"
          >
            <h4>{{ unit.UnitKenmerk }} {{ unit.Titel }} ({{ unit.Kenmerk }})</h4>
            <h5>
              {{ unit.Adres }}&nbsp;&nbsp;{{ unit.WijkPlaats }}
              <small>regio {{ unit.Regio }}</small>
            </h5>
          </b-col>
          <b-col>
            <b-button-toolbar class="float-right">
              <b-input-group
                class="mr-1"
                size="sm"
              >
                <b-input-group-prepend
                  is-text
                  v-b-tooltip.hover
                  title="Onderhoudsfirma"
                >
                  <font-awesome-icon icon="wrench" />
                </b-input-group-prepend>
                <b-input-group-text>
                  {{ onderhoudsfirmaNaam }} · {{ unit.Onderhoudsnummer }}
                </b-input-group-text>
              </b-input-group>
              <b-input-group
                class="mr-1"
                size="sm"
              >
                <b-input-group-prepend
                  is-text
                  v-b-tooltip.hover
                  :title="'Keuringsinstantie: ' + unit.Keuringsinstantie"
                >
                  <font-awesome-icon icon="clipboard-check" />
                </b-input-group-prepend>
                <b-input-group-text>{{ unit.Keuringsnummer }}</b-input-group-text>
                <b-input-group-text
                  :class="['bg-' + keuringcolor[actueleKeuringsstatus + 3]]"
                  v-b-tooltip.hover
                  :title="cerificaat(actueleKeuringsstatus)"
                >
                  <font-awesome-icon :icon="keuringicon[actueleKeuringsstatus]" class="mr-1" />
                  {{ moment(actueleKeuringsstatus == -3 ? unit.Keuringsdeadline : unit.Keuringsdatum).format('L') }}
                  <span
                    v-if="actueleKeuringsstatus == 1"
                    class="text-warning ml-1"
                  >
                    → {{ moment(unit.Keuringsdeadline).format('L') }}
                  </span>
                </b-input-group-text>
              </b-input-group>
            </b-button-toolbar>
          </b-col>
        </b-form-row>
      </b-card-header>
      <b-card-header>
        <b-button-toolbar class="pull-right">
          <b-input-group class="mr-1">
            <b-input-group-prepend>
              <b-button
                variant="outline-secondary"
                :disabled="!previousUnitRoute"
                v-b-tooltip.hover
                :title="previousUnitRoute ? 'Vorige unit' : 'Geen vorige unit'"
                @click="gaNaarUnit(previousUnitRoute)"
              >
                <font-awesome-icon icon="chevron-left" />
              </b-button>
              <b-input-group-text
                v-b-tooltip.hover
                :title="'Complex: ' + unit.Complex.ComplexKenmerk"
              >
                <font-awesome-icon icon="building" />
              </b-input-group-text>
            </b-input-group-prepend>
            <b-select
              :value="unit.Id"
              :options="complexUnitRoutes"
              @change="id => gaNaarUnitById(id)"
            />
            <b-input-group-append>
              <b-button
                variant="outline-secondary"
                :disabled="!nextUnitRoute"
                v-b-tooltip.hover
                :title="nextUnitRoute ? 'Volgende unit' : 'Geen volgende unit'"
                @click="gaNaarUnit(nextUnitRoute)"
              >
                <font-awesome-icon icon="chevron-right" />
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <b-input-group class="w-10">
            <b-input-group-prepend
              is-text
              v-b-tooltip.hover
              title="Status"
            >
              <font-awesome-icon icon="info-circle" />
            </b-input-group-prepend>
            <unit-status 
              :status="unit.HuidigeStatus" 
              @status-changed="onStatusChanged($event)"
            />
            <b-input-group-append
              is-text
              v-b-tooltip.hover
              title="Keuringsalarm"
            >
              <b-form-checkbox
                v-model="unit.Keuringsalarm"
                :disabled="unit.HuidigeStatus < 2"
                class="mr-n2"
                size="sm"
                value="1"
                unchecked-value="0"
              >
                <font-awesome-icon icon="bell" />
              </b-form-checkbox>
            </b-input-group-append>
          </b-input-group>

          <b-input-group class="w-10 mx-1">
            <b-input-group-prepend
              is-text
              v-b-tooltip.hover
              title="Actuele prognose"
            >
              <font-awesome-icon icon="chart-line" />
            </b-input-group-prepend>
            <b-select
              v-model="unit.HuidigePrognose"
              :options="prognoseOptions"
              text-field="Omschrijving"
              value-field="Id"
              style="width:180px;"
            >
              <template #first>
                <option 
                  value="null" 
                  disabled
                >
                  -- Kies een prognose --
                </option>
              </template>
            </b-select>
            <b-input-group-append>
              <b-button
                v-b-tooltip.hover
                title="Prognose wissen"
                @click="unit.HuidigePrognose = null"
              >
                <font-awesome-icon icon="times" />
              </b-button>
            </b-input-group-append>
          </b-input-group>
           
          <export-button-unit
            :filename="unit.UnitKenmerk"
            class="ml-auto"
          />

          <b-button 
            :disabled="isBusy" 
            variant="success" 
            class="ml-1" 
            v-b-tooltip.hover
            title="Opslaan unit"
            @click="saveUnit"
          >
            <font-awesome-icon
              v-if="!isBusy"
              icon="save"
            />
            <b-spinner
              v-else
              :small="true"
              size="sm"
              role="status"
              variant="secondary"
            />
          </b-button>
        </b-button-toolbar>
      </b-card-header>
      <b-card-body>
        <b-nav tabs>
          <b-nav-item
            v-for="tab in tabs.concat(unit.HuidigePrognose ? unitOpleveringPrognose : [])"
            :key="tab.label"
            :title="tab.label"
            :to="tab.to"
            :active="$route.name == tab.label"
            :disabled="tab.disabled"
          >
            <i :class="tab.icon" />
            {{ tab.label }}
          </b-nav-item>
        </b-nav>
      </b-card-body>

      <b-card-body>
        <router-view :unit="unit" />
      </b-card-body>
      <b-card-footer>
        <delete-button 
          v-if="isBeheerder" 
          @confirmed="deleteUnit"
        />
      </b-card-footer>
    </b-card>
    <b-alert 
      v-else 
      :variant="status.variant" 
      show
    >
      {{ status.error }}
    </b-alert>
  </b-overlay>
</template>
<script>
import ExportButtonUnit from "./export/ExportButtonUnit";
import DeleteButton from "@/components/DeleteButton";
import UnitStatus from "@/components/UnitStatus";
import { serviceMixin } from '../mixins/serviceMixin';
import { keuringMixin } from '../mixins/keuringMixin';
import FontAwesome from './icons/FontAwesome.vue';
import moment from 'moment';

export default {
  components: {
    DeleteButton,
    ExportButtonUnit,
    UnitStatus,
    FontAwesome
  },
  mixins: [serviceMixin, keuringMixin],
  beforeRouteUpdate (to, from, next) {
    if (to.params.id != from.params.id) this.getUnit(to.params.id);
    next();
  },
  props: {
    id: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isBusy: false,
      unit: null,
      status: {
        variant: "info",
        label: "Ophalen",
        error: null
      },
      tabs: [
        {
          label: "Dashboard",
          icon: ["cui-dashboard"],
          to: {
            name: "DashboardUnit"
          }
        },
        {
          label: "Algemeen",
          icon: ["cui-pencil"],
          to: {
            name: "Algemeen"
          }
        },
        {
          label: "Inventarisatie",
          icon: ["cui-list"],
          to: {
            name: "Inventarisatie"
          }
        },
        {
          label: "Prognoses",
          icon: ["cui-cog"],
          to: {
            name: "Prognoses"
          }
        },
        {
          label: "Documenten",
          icon: ['fa', 'fa-file'],
          to: {
            name: "DocumentenUnit"
          }
        },
        {
          label: "Fotoboek",
          icon: ['fa','fa-photo'],
          to: {
            name: "Fotoboek"
          }
        },
        {
          label: "Planning",
          icon: ['fa', 'fa-calendar'],
          to: {
            name: "Planning"
          }
        },
        {
          label: "KPI",
          icon: ['fa', 'fa-table'],
          to: {
            name: "KPI"
          }
        },
        {
          label: "Service",
          icon: ['fa', 'fa-clipboard'],
          to: {
            name: "Service"
          }
        },
        
        {
          label: "Export",
          icon: ['fa', 'fa-upload'],
          to: {
            name: "Export"
          }
        },
        
        {
          label: "Units",
          icon: ['fa', 'fa-th-list'],
          to: {
            name: "RelatieUnit"
          }
        },
      ]
    };
  },

  computed: {
    isBeheerder: function() {
      return (this.$store.getters.user.roles || '').split(",").includes('Beheerder');
    },
    onderhoudsfirmaNaam: function() {
      if (this.unit.Onderhoudsfirma) {
        this.$store.dispatch('relaties/ensureLoaded', this.unit.Onderhoudsfirma);
      }
      return this.$store.getters['relaties/relatieNaam'](this.unit.Onderhoudsfirma) || 'Geen';
    },
    actueleKeuringsstatus() {
      var deadline = moment(this.unit.Keuringsdeadline);
      if (this.unit.KeuringsStatus !== 1) return this.unit.KeuringsStatus;
      return moment() > deadline ? -3 : 1
    },  
    unitOpleveringPrognose: function() {
      return this.unit ? 
      {
        label: "Opleveringen",
        icon: ["fa","fa-clipboard"],
        disabled: this.unit.HuidigePrognose == null,
        to: {
          name: "Opleveringen",
        }
      } : 
      null
    },
    prognoseLabel: function() {
      return this.unit.Prognoses.length + " prognoses";
    },
    prognoseOptions: function() {
      var vm = this;
      return this.unit.Prognoses.map(function(item) {
        return {
          'Omschrijving': item.Omschrijving ? item.Omschrijving : `${item.Inspecteur} op ${ vm.moment(item.Datum).format('LL')}`,
          'Id' : item.Id
        }
      });
    },
    complexUnitRoutes: function() {
      var currentRoute = this.$route.name.toLowerCase()
      var isPrognoseRoute = currentRoute == 'prognose'

      var complexKenmerk = this.unit.Complex.ComplexKenmerk;
      return this.unit.Complex.Units.map(u => {
        var kenmerk = u.Kenmerk && u.Kenmerk.indexOf(complexKenmerk) === 0
          ? u.Kenmerk.slice(complexKenmerk.length)
          : u.Kenmerk;
        return {
          text: `${kenmerk} ${u.Adres}`,
          value: u.Id,
          disabled: isPrognoseRoute && !u.HuidigePrognose
        }})
    },
    currentUnitIndex: function() {
      return this.unit.Complex.Units.findIndex(u => u.Id == this.unit.Id);
    },
    previousUnitRoute: function() {
      var idx = this.currentUnitIndex;
      if (idx <= 0) return null;
      var u = this.unit.Complex.Units[idx - 1];
      return { name: this.$route.name, params: { id: u.Id, prognoseid: u.HuidigePrognose } };
    },
    nextUnitRoute: function() {
      var units = this.unit.Complex.Units;
      var idx = this.currentUnitIndex;
      if (idx < 0 || idx >= units.length - 1) return null;
      var u = units[idx + 1];
      return { name: this.$route.name, params: { id: u.Id, prognoseid: u.HuidigePrognose } };
    }
  },
  mounted() {
    this.getUnit(this.$route.params.id);
  },
  methods: {
    gaNaarUnit(selectie) {
      console.log(selectie);
      this.$router.push(selectie)
    },
    gaNaarUnitById(id) {
      if (!id || id == this.unit.Id) return;
      var u = this.unit.Complex.Units.find(x => x.Id == id);
      if (!u) return;
      this.gaNaarUnit({ name: this.$route.name, params: { id: u.Id, prognoseid: u.HuidigePrognose } });
    },
    cerificaat(status) {
      return this.keuringstatus.find((el) => el.value == status).text
    },
    deleteUnit() {
      // alert('Unit verwijdering bevestigd!')
      var vm = this;
      this.delete('/unit/' + this.$props.id).then(
        (resp) => {
          console.log(resp)
          if (resp.data.result.deleted) {
            // navigate to main
            vm.$router.replace({path: '/units/'})
          } else {
            vm.status.error = resp.data.result.message;
            vm.status.variant = "danger";
          }
        }
      ).catch((e) => {
        vm.status.error = e;
        vm.status.variant = "danger";
      })
    },
    onStatusChanged(status) {
      this.unit.HuidigeStatus = status;
      if (status < 2) this.unit.Keuringsalarm = status;
    },
    saveUnit() {
      var vm = this;
      this.isBusy = true;
      return this.$http
        .post("/unit/" + this.$props.id, this.unit)
        .then(function(response) {
          console.log("unit result");
          return response.data;
        })
        .catch(function(error) {
          alert(error);
          return;
        })
        .then(function() {
          vm.isBusy = false;
        });
    },
    getUnit(id) {
      var vm = this;
      console.log(this.$route.path);
      vm.isBusy = true;
      this.$http
        .get("/unit/" + id)
        .then(function(response) {
          console.log("unit result");
          console.log( response.data );
          vm.unit = response.data.unit;
          vm.$store.dispatch("addUnitToHistory",{
            unit: {
              kenmerk: vm.unit.UnitKenmerk,
              adres: vm.unit.Adres,
              id: vm.unit.Id,
              remoteid: vm.unit.RemoteId,
              plaats: vm.unit.WijkPlaats
            } 
          });
        })
        .catch(function(error) {
          vm.status.error = error.response.data.error;
          vm.status.variant = "danger";
        })
        .finally(() => vm.isBusy = false);
    }
  }
};
</script>